import React from "react";
import {
  FaEnvelope,
  FaLinkedin,
  FaGlobe,
  FaMapMarkerAlt,
  FaTrophy,
  FaGraduationCap,
  FaCode,
  FaBookOpen,
  FaBriefcase,
  FaLanguage,
  FaHeart,
} from "react-icons/fa";
import "./ModernProfessionalTemplate.css";

const ModernProfessionalTemplate = ({ resumeData }) => {
  if (!resumeData) return <div className="loading-placeholder">Loading...</div>;

  const {
    personal = {},
    experience = [],
    education = [],
    skills = { technical: [], soft: [], languages: [] },
    achievements = [],
    certifications = [],
    interests = [],
  } = resumeData;

  const allSkills = [...skills.technical, ...skills.soft];

  // Helper to split achievement into title and description
  const parseAchievement = (ach) => {
    // If achievement contains a period or colon, split there
    const match = ach.match(/^([^.:!?]+[.:!?])\s*(.*)/);
    if (match) {
      return { title: match[1].trim(), description: match[2].trim() };
    }
    // Otherwise, use first 50 chars as title
    return { title: ach.slice(0, 50), description: ach };
  };

  return (
    <div className="resume-template modern-professional">
      {/* LEFT COLUMN (Dark) */}
      <div className="mp-left">
        {/* Key Achievements */}
        {achievements.length > 0 && (
          <div className="mp-section">
            <h3 className="mp-section-title">
              <FaTrophy /> KEY ACHIEVEMENTS
            </h3>
            {achievements.map((ach, idx) => {
              const { title, description } = parseAchievement(ach);
              return (
                <div key={idx} className="mp-achievement">
                  <div className="mp-ach-title">{title}</div>
                  <div className="mp-ach-desc">{description}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Skills */}
        {allSkills.length > 0 && (
          <div className="mp-section">
            <h3 className="mp-section-title">
              <FaCode /> SKILLS
            </h3>
            <div className="mp-skills">
              {allSkills.map((skill, idx) => (
                <span key={idx} className="mp-skill-tag">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Training / Courses (Certifications) */}
        {certifications.length > 0 && (
          <div className="mp-section">
            <h3 className="mp-section-title">
              <FaBookOpen /> TRAINING / COURSES
            </h3>
            {certifications.map((cert, idx) => (
              <div key={cert.id || idx} className="mp-course">
                <div className="mp-course-title">{cert.name}</div>
                <div className="mp-course-issuer">{cert.issuer}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* RIGHT COLUMN (White) */}
      <div className="mp-right">
        {/* Header: Name + Role + Contact */}
        <div className="mp-header">
          <h1 className="mp-name">{personal.fullName || "YOUR NAME"}</h1>
          <div className="mp-role">
            {personal.title || "The role you are applying for?"}
          </div>
          <div className="mp-contact">
            {personal.email && (
              <span>
                <FaEnvelope /> {personal.email}
              </span>
            )}
            {personal.address && (
              <span>
                <FaMapMarkerAlt /> {personal.address}
              </span>
            )}
            {(personal.linkedin || personal.portfolio) && (
              <span>
                {personal.linkedin && <FaLinkedin />}{" "}
                {personal.portfolio && <FaGlobe />}
                {personal.linkedin ? " LinkedIn" : ""}
                {personal.linkedin && personal.portfolio ? " / " : ""}
                {personal.portfolio ? " Portfolio" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Summary */}
        {personal.summary && (
          <div className="mp-section-right">
            <h3 className="mp-section-title-right">SUMMARY</h3>
            <div className="mp-summary">{personal.summary}</div>
          </div>
        )}

        {/* Experience */}
        {experience.length > 0 && (
          <div className="mp-section-right">
            <h3 className="mp-section-title-right">
              <FaBriefcase /> EXPERIENCE
            </h3>
            {experience.map((exp, idx) => (
              <div key={exp.id || idx} className="mp-exp-item">
                <div className="mp-exp-title">{exp.title || "Title"}</div>
                <div className="mp-exp-company">
                  {exp.company || "Company Name"}
                </div>
                <div className="mp-exp-meta">
                  <span>{exp.startDate || "Date period"}</span>
                  <span>{exp.location || "Location"}</span>
                </div>
                <ul className="mp-exp-desc">
                  {exp.description?.split("\n").map((line, i) => (
                    <li key={i}>
                      {line ||
                        "Highlight your accomplishments, using numbers if possible."}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* Education */}
        {education.length > 0 && (
          <div className="mp-section-right">
            <h3 className="mp-section-title-right">
              <FaGraduationCap /> EDUCATION
            </h3>
            {education.map((edu, idx) => (
              <div key={edu.id || idx} className="mp-edu-item">
                <div className="mp-edu-degree">
                  {edu.degree || "Degree and Field of Study"}
                </div>
                <div className="mp-edu-school">
                  {edu.institution || "School or University"}
                </div>
                <div className="mp-edu-meta">
                  <span>{edu.year || "Date period"}</span>
                  <span>{edu.location || "Location"}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Languages */}
        {skills.languages?.length > 0 && (
          <div className="mp-section-right">
            <h3 className="mp-section-title-right">
              <FaLanguage /> LANGUAGES
            </h3>
            <div className="mp-languages">
              {skills.languages.map((lang, idx) => (
                <span key={idx} className="mp-lang-item">
                  {lang}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Interests */}
        {interests.length > 0 && (
          <div className="mp-section-right">
            <h3 className="mp-section-title-right">
              <FaHeart /> INTERESTS
            </h3>
            {interests.map((interest, idx) => (
              <div key={idx} className="mp-interest-item">
                <div className="mp-interest-title">{interest}</div>
                <div className="mp-interest-desc">
                  What does the company have which makes it attractive for you?
                  (e.g. learning opportunities)
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModernProfessionalTemplate;
